import { useEffect, useRef } from 'react';
import { TURNSTILE_SCRIPT_URL, getTurnstileSiteKey } from '@/lib/turnstile';

let scriptPromise = null;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Turnstile failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

// Renders nothing unless REACT_APP_TURNSTILE_SITE_KEY is set. Tokens are single use:
// remount (change `key`) after each submit to get a fresh challenge.
const TurnstileWidget = ({ onToken }) => {
  const containerRef = useRef(null);
  const siteKey = getTurnstileSiteKey();

  useEffect(() => {
    if (!siteKey) return undefined;
    let widgetId = null;
    let cancelled = false;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(''),
        });
      })
      .catch((error) => console.error(error));

    return () => {
      cancelled = true;
      if (widgetId !== null && window.turnstile) window.turnstile.remove(widgetId);
      onToken('');
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" className="min-h-[65px]" />;
};

export default TurnstileWidget;
